import {
  Author,
  FetchStyleboardResponse,
  ImageType,
  NotificationType,
  PublicStyleboardSort,
  PutItemToStyleboardRequest,
  StyleboardSuccessLocalState,
} from 'features/Styleboard/types'

import {
  fetchStyleboard,
  fetchStyleboardForAnonymousUser,
  fetchStyleboards,
  fetchFollowStyleboards,
  followStyleboard,
  unfollowStyleboard,
  putStyleboard,
  deleteStyleboard,
  putProfile,
  addImage,
  deleteItemFromStyleboard,
  fetchMoreData,
  fetchPopularStyleboards,
  fetchSimilarItems,
  putItemToStyleboard,
  fetchPublicStyleboards,
} from '../../requests'
import actions from './actions'
import { getCorrectUrlForPagination } from './helpers'

export const operationResetStyleboardState =
  () =>
  (dispatch): void =>
    dispatch(actions.resetStyleboardState())

export const operationFetchStyleboard =
  (styleboardId: string, shouldShowSpinner: boolean, link?: string) =>
  async (dispatch): Promise<void> => {
    if (shouldShowSpinner) {
      dispatch(actions.fetchStyleboardStart())
    }

    try {
      const newLink = getCorrectUrlForPagination(link)
      const styleboardResponse = await fetchStyleboard(styleboardId, newLink)

      const response = {
        styleboard: styleboardResponse.styleboard,
        items: styleboardResponse.items,
      } as FetchStyleboardResponse

      dispatch(actions.fetchStyleboardSuccess(response))
    } catch (e) {
      dispatch(actions.fetchStyleboardFail(e))
    }
  }

export const operationFetchStyleboardForAnonymousUser =
  (styleboardId: string, shouldShowSpinner: boolean, link?: string) =>
  async (dispatch): Promise<void> => {
    if (shouldShowSpinner) {
      dispatch(actions.fetchStyleboardStart())
    }

    try {
      const newLink = getCorrectUrlForPagination(link)
      const styleboardResponse = await fetchStyleboardForAnonymousUser(styleboardId, newLink)

      const response = {
        styleboard: styleboardResponse.styleboard,
        items: styleboardResponse.items,
      } as FetchStyleboardResponse

      dispatch(actions.fetchStyleboardSuccess(response))
    } catch (e) {
      dispatch(actions.fetchStyleboardFail(e))
    }
  }

export const operationFetchMoreData =
  (link: string, shouldShowSpinner: boolean) =>
  async (dispatch): Promise<void> => {
    if (shouldShowSpinner) {
      dispatch(actions.fetchMoreDataStart())
    }

    try {
      const newLink = getCorrectUrlForPagination(link)
      const response = await fetchMoreData(newLink)
      dispatch(actions.fetchMoreDataSuccess(response))
    } catch (error) {
      dispatch(actions.fetchMoreDataFail(error))
    }
  }

export const operationFetchSimilarItems =
  (styleboardId: string) =>
  async (dispatch): Promise<void> => {
    dispatch(actions.fetchSimilarItemsStart())

    try {
      const response = await fetchSimilarItems(styleboardId)
      dispatch(actions.fetchSimilarItemsSuccess(response))
    } catch (e) {
      dispatch(actions.fetchSimilarItemsFail(e))
    }
  }

export const operationResetSimilarItemsState =
  () =>
  (dispatch): void =>
    dispatch(actions.resetSimilarItemsState())

export const operationFetchStyleboards =
  (link?: string, mergeDeep: boolean = true) =>
  async (dispatch): Promise<void> => {
    dispatch(actions.fetchStyleboardsStart())

    try {
      const newLink = getCorrectUrlForPagination(link)
      const styleboardResponse = await fetchStyleboards(newLink)
      dispatch(actions.fetchStyleboardsSuccess({ ...styleboardResponse, mergeDeep: !!mergeDeep }))
    } catch (e) {
      dispatch(actions.fetchStyleboardsFail(e))
      dispatch(actions.addNotification(NotificationType.FETCH_STYLEBOARDS_FAILED))
    }
  }

export const operationFetchFollowStyleboards =
  (link?: string, mergeDeep: boolean = true) =>
  async (dispatch): Promise<void> => {
    dispatch(actions.fetchFollowStyleboardsStart())

    try {
      const newLink = getCorrectUrlForPagination(link)
      const styleboardResponse = await fetchFollowStyleboards(newLink)
      dispatch(
        actions.fetchFollowStyleboardsSuccess({ ...styleboardResponse, mergeDeep: !!mergeDeep }),
      )
    } catch (e) {
      dispatch(actions.fetchFollowStyleboardsFail(e))
      dispatch(actions.addNotification(NotificationType.FETCH_FOLLOW_STYLEBOARDS_FAILED))
    }
  }

export const operationFetchPopularStyleboards =
  () =>
  async (dispatch): Promise<void> => {
    dispatch(actions.fetchPopularStyleboardsStart())

    try {
      const response = await fetchPopularStyleboards()
      dispatch(actions.fetchPopularStyleboardsSuccess(response))
    } catch (e) {
      dispatch(actions.fetchPopularStyleboardsFail(e))
      dispatch(actions.addNotification(NotificationType.FETCH_FOLLOW_STYLEBOARDS_FAILED))
    }
  }

export const operationFollowStyleboard =
  (styleboardId: string) =>
  async (dispatch): Promise<void> => {
    dispatch(actions.followStyleboardStart())

    try {
      await followStyleboard(styleboardId)

      dispatch(actions.followStyleboardSuccess())
    } catch (e) {
      dispatch(actions.followStyleboardFail(e))
      dispatch(actions.addNotification(NotificationType.FOLLOW_STYLEBOARD_FAILED))
    }
  }

export const operationUnfollowStyleboard =
  (styleboardId: string) =>
  async (dispatch): Promise<void> => {
    dispatch(actions.unfollowStyleboardStart())

    try {
      await unfollowStyleboard(styleboardId)

      dispatch(actions.unfollowStyleboardSuccess())
    } catch (e) {
      dispatch(actions.unfollowStyleboardFail(e))
    }
  }

export const operationSaveStyleboard =
  (styleboardId: string, updateObject: StyleboardSuccessLocalState) =>
  async (dispatch): Promise<void> => {
    dispatch(actions.putStyleboardStart())

    try {
      await putStyleboard(styleboardId, updateObject.title, updateObject.about, updateObject.status)

      dispatch(actions.putStyleboardSuccess(updateObject))
      dispatch(actions.addNotification(NotificationType.PUT_STYLEBOARD_SUCCEDED))
    } catch (e) {
      dispatch(actions.putStyleboardFailed(e))
    }
  }

export const operationDeleteStyleboard =
  (styleboardId: string) =>
  async (dispatch): Promise<void> => {
    dispatch(actions.deleteStyleboardStart())

    try {
      await deleteStyleboard(styleboardId)

      dispatch(actions.deleteStyleboardSuccess(styleboardId))
    } catch (e) {
      dispatch(actions.deleteStyleboardFailed(e))
      dispatch(actions.addNotification(NotificationType.STYELBOARD_DELETED_FAILED))
    }
  }

export const operationPutAuthor =
  (author: Author) =>
  async (dispatch): Promise<void> => {
    dispatch(actions.putAuthorStart())

    try {
      await putProfile(author)

      dispatch(actions.putAuthorSuccess(author.userName))
      dispatch(actions.addNotification(NotificationType.PUT_PROFILE_SUCCEEDED))
    } catch (e) {
      dispatch(actions.putAuthorFail(e))
      dispatch(actions.addNotification(NotificationType.PUT_STYLEBOARD_FAILED))
    }
  }

export const operationPutItemTostyleboard =
  (body: PutItemToStyleboardRequest) =>
  async (dispatch): Promise<void> => {
    dispatch(actions.putItemToStyleboardStart())

    try {
      await putItemToStyleboard(body)
      dispatch(actions.putItemToStyleboardSuccess(body.items[0].article.code))
    } catch (e) {
      dispatch(actions.putItemToStyleboardFailed(e))
      dispatch(actions.addNotification(NotificationType.PUT_STYLEBOARD_FAILED))
    }
  }

export const operationAddImage =
  (
    image: File,
    description: string,
    type: ImageType,
    styleboardId?: string,
    notificationType?: NotificationType,
    notificationTypeFailure?: NotificationType,
  ) =>
  async (dispatch): Promise<void> => {
    dispatch(actions.addImageStart())

    try {
      await addImage(image, description, type, styleboardId)

      dispatch(actions.addImageSuccess())
      if (notificationType) dispatch(actions.addNotification(notificationType))
    } catch (e) {
      dispatch(actions.addImageFail(e))
      // TODO API will return label key in some cases that we can use to display error
      if (notificationTypeFailure) dispatch(actions.addNotification(notificationTypeFailure))
    }
  }

export const operationDeleteItemFromStyleboard =
  (styleboardId: string, itemId: string) =>
  async (dispatch): Promise<void> => {
    dispatch(actions.deleteItemFromStyleboardStart())

    try {
      await deleteItemFromStyleboard(styleboardId, itemId)

      dispatch(actions.deleteItemFromStyleboardSuccess(itemId))
    } catch (e) {
      dispatch(actions.deleteItemFromStyleboardFailed(e))
      dispatch(actions.addNotification(NotificationType.STYLEBOARD_ITEM_DELETE_FAILED))
    }
  }

export const operationChangeBackgroundImage =
  (image: File, type: ImageType, styleboardId: string) =>
  async (dispatch): Promise<void> => {
    const newImageUrl = URL.createObjectURL(image)

    try {
      await addImage(image, '', type, styleboardId)
      dispatch(actions.changeBackgroundImageSuccess(newImageUrl))
    } catch (e) {
      dispatch(actions.addNotification(NotificationType.CHANGE_BACKGROUND_IMAGE_FAILED))
    }
  }

export const operationFetchPublicStyleboards =
  (sortBy: PublicStyleboardSort, link?: string, mergeDeep: boolean = true) =>
  async (dispatch): Promise<void> => {
    dispatch(actions.fetchPublicStyleboardsStart())

    try {
      if (link) {
        const newLink = getCorrectUrlForPagination(link)
        const styleboardResponse = await fetchPublicStyleboards(sortBy, newLink)
        dispatch(
          actions.fetchPublicStyleboardsSuccess({ ...styleboardResponse, mergeDeep: !!mergeDeep }),
        )
      } else {
        const styleboardResponse = await fetchPublicStyleboards(sortBy)
        dispatch(actions.fetchPublicStyleboardsSuccess({ ...styleboardResponse, mergeDeep: false }))
      }
    } catch (e) {
      dispatch(actions.fetchPublicStyleboardsFail(e))
      dispatch(actions.addNotification(NotificationType.FETCH_STYLEBOARDS_FAILED))
    }
  }
